/* eslint-disable @typescript-eslint/no-explicit-any */
// Imports for external libraries go here.
import { FC, useEffect, useState, useRef } from 'react';
import { useApolloClient } from '@apollo/client';

import MapClusterComponent from '../MapClusterComponent';
import {
  LEISURE_REGION_LIST,
  SELECTED_REGION,
  STATE_COUNTRY_LIST,
  SHOW_PAGE_LEVEL_ALERT,
  DEFAULT_ZOOM_LEVEL,
  TRACKING_CONST,
  SHOW_REGION_CARD_ERROR,
  TOTAL_NO_OF_HOTELS_WORLDWIDE,
  AFRICA_CUSTOM_LABEL,
  ASIA_CUSTOM_LABEL,
  EUROPE_CUSTOM_LABEL,
  MIDDLE_EAST_CUSTOM_LABEL,
  SOUTH_AMERICA_CUSTOM_LABEL,
  CARIBBEAN_CUSTOM_LABEL,
  CENTRAL_AMERICA_CUSTOM_LABEL,
  AUSTRALIA_CUSTOM_LABEL,
  USA_CUSTOM_LABEL,
  MIDDLE_EAST_CODE,
  SOUTH_AMERICA_CODE,
  CENTRAL_AMERICA_CODE,
  AUSTRALIA_CODE,
  USA_CODE,
} from '../../../modules/constants/HotelDirectory.constants';
import { useHotelDirectoryStore } from '../../../modules/store/hotelDirectoryStore';
import {
  customSort,
  extractFiltersData,
  extractMarkerData,
  searchCounterValue,
  updateSessionProperties,
  getRegionDataToStore,
  convertToDTTResponse,
} from './HotelDirectoryMapHelper';
import { StyledHotelDirectoryMap } from './HotelDirectoryMap.styles';
import { phoenixShopSearchPropertiesByLocationInput } from '../queries';
import {
  HotelDirectoryDataList,
  HotelDirectoryMapProps,
  RegionItems,
  tooltipContentProps,
  UXLPayload,
} from './HotelDirectoryMap.types';
import { useHotelDirectoryStaticDataContext } from '../../../modules/context/HotelDirectoryStaticDataContext';
import { SearchFacetsList } from '../../../constants/Filters.constant';
import { formatNumberByLocale } from '../../../utils/src/utils';
import { useConditionalPageContextSERP } from '../../../hooks';
import { hotelDirectoryMockUXL } from '../__mock__/hotelDirectoryMockUXL.';
import { SkeletonLoader } from '../../../molecules/SkeletonLoader';
import { updateDataLayerOnInteractions } from '../../../modules/utils';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { MAP_VIEW_SESSION_KEY } from '../../../modules/store/store.constants';
import dynamic from 'next/dynamic';
const SearchFilterPanel = dynamic(() =>
  import('../../../molecules/SearchFilterPanel').then(module => module.SearchFilterPanel)
);

const HOTEL_DIRECTORY_TOOLTIP_ID = 'tooltip-dialog-hotel-directory';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    google: any;
  }
}
export const HotelDirectoryMap: FC<HotelDirectoryMapProps> = (props: HotelDirectoryMapProps) => {
  const {
    model,
    isDesktopView,
    setTotalCountOnHeader,
    filtersCount,
    filtersSessionVal,
    filtersPayload,
    setFiltersPayload,
    setCenter,
    center,
    setPillsData,
    isAmenitiesSelected,
    isBrandsSelected,
    setFiltersData,
    popupOpenState,
    setPopupOpenState,
    filterButtonClicked,
    amenitiesData,
    brandsData,
    setFilterButtonClicked,
    filtersData,
    setFiltersCount,
    setIsDataLoaded,
    isStoryBook,
    sessionData,
    isViewportM,
  } = props;
  const regionName = model?.mapRegion ? JSON.parse(model?.mapRegion) : [];
  const subRegionName = model?.subRegion ? JSON.parse(model?.subRegion) : {};
  const labelForChina = model?.chinaDisplayLabel;
  const [totalCountOfHotels, setTotalCountOfHotels] = useState(0);
  const {
    headersData,
    currentLocale,
    SESSION_APP_GET_CALL_URL,
    SESSION_GET_CALL_URL_CLIENT,
    resolvedUrl,
    query,
    datalayerParams,
    datalayerProperties,
    requestId,
    isAuthorMode,
  } = useConditionalPageContextSERP();

  const envVarsPrefix = useClientEnvVarsStore(state => state.envVarsObject['NEXT_PUBLIC_PREFIX']);
  const NEXT_PUBLIC_PREFIX = process.env['NEXT_PUBLIC_PREFIX'] || envVarsPrefix;
  const setHotelDirectoryState = useHotelDirectoryStore(
    (state: { setHotelDirectoryState: any }) => state.setHotelDirectoryState
  );
  const selectedRegion = useHotelDirectoryStore(
    (state: { selectedRegion: any }) => state.selectedRegion
  ).selectedRegion;
  const stateORCountryList = useHotelDirectoryStore(
    (state: { stateORCountryList: any }) => state.stateORCountryList
  ).stateORCountryList;

  const { HOTEL_DIRECTORY, ALL_FILTERS, AMENITIES_FILTERS, BRANDS_FILTERS } = TRACKING_CONST;

  const [openInfoWindow, setOpenInfoWindow] = useState(false);
  const [markers, setMarkers] = useState<HotelDirectoryDataList>([]);
  const [infoWindowData, setInfoWindowData] = useState<any>({ lat: 0, lng: 0 });
  const [mapZoom, setMapZoom] = useState<number>(DEFAULT_ZOOM_LEVEL);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [disableFilters, setDisableFilters] = useState<boolean>(false);

  const isFiltersApplied = filtersCount > 0 ? 'true' : 'false';

  const [applyBtnClicked, setApplyBtnClicked] = useState<boolean>(false);
  const client = useApolloClient();
  // Note: Removed clustererRef to satisfy React Compiler - now managed internally by child component
  const selectedRegionRef = useRef<any>();
  const filtersPayloadRef = useRef<any>();
  const [tooltipPosition, setTooltipPosition] = useState({
    offsetLeft: 0,
    offsetTop: 0,
    tooltipDialogElemOffsetHeight: 0,
    tooltipDialogElemOffsetWidth: 0,
    anchorOffset: 0,
  });
  const HOTEL_DIRECTORY_TOOLTIP_ID = 'tooltip-dialog-hotel-directory';

  const {
    applyLabel,
    clearLabel,
    filtersOrderList,
    brandCategories,
    filterLabel,
    brandsLabel,
    amenitiesLabel,
    showMoreLabel,
    showLessLabel,
    memberLoungeTooltipLabel,
  } = useHotelDirectoryStaticDataContext();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const bodyRef = useRef<HTMLBodyElement | null>(null);

  useEffect(() => {
    bodyRef.current = document.body as HTMLBodyElement;
  }, []);

  // function to update the URL parameters based on the number of filters selected
  function updateURLParams() {
    // case where >=1 filter(s) have been selected ("?filtersApplied=true" has to be set)
    const params = new URLSearchParams(window?.location?.search ?? '');
    if (filtersCount > 0) {
      params.set('filtersApplied', 'true');
    }
    // case where 0 filters have been selected (filtersApplied param has to be removed)
    else {
      if (params.has('filtersApplied')) {
        params.delete('filtersApplied');
      }
    }
    const counterValue = sessionStorage.getItem('hotelDirectoryCounter');
    const correctURL = filtersCount > 0 ? `?${params}/#${counterValue}/` : `?${params}#${counterValue}/`;
    window.history.replaceState({}, '', decodeURIComponent(correctURL));
  }

  // function to update the exit point URLs
  function updateExitPointLink() {
    sessionStorage.setItem('isFiltersApplied', isFiltersApplied);
  }

  useEffect(() => {
    if (disableFilters) {
      setHotelDirectoryState([SHOW_PAGE_LEVEL_ALERT], {
        [SHOW_PAGE_LEVEL_ALERT]: { [SHOW_PAGE_LEVEL_ALERT]: true },
      });
    } else {
      setHotelDirectoryState([SHOW_PAGE_LEVEL_ALERT], {
        [SHOW_PAGE_LEVEL_ALERT]: { [SHOW_PAGE_LEVEL_ALERT]: false },
      });
    }
  }, [disableFilters]);

  useEffect(() => {
    //initializing store with region labels with ZERO count in case UXL fails to return the count and labels
    const regionDataToStore: HotelDirectoryDataList = [];
    regionName?.forEach((item: RegionItems) => {
      let customLabel = '';
      if (item.regionCode === AFRICA_CUSTOM_LABEL) {
        customLabel = AFRICA_CUSTOM_LABEL;
      } else if (item.regionCode === ASIA_CUSTOM_LABEL) {
        customLabel = ASIA_CUSTOM_LABEL;
      } else if (item.regionCode === EUROPE_CUSTOM_LABEL) {
        customLabel = EUROPE_CUSTOM_LABEL;
      } else if (item.regionCode === MIDDLE_EAST_CODE) {
        customLabel = MIDDLE_EAST_CUSTOM_LABEL;
      } else if (item.regionCode === SOUTH_AMERICA_CODE) {
        customLabel = SOUTH_AMERICA_CUSTOM_LABEL;
      } else if (item.regionCode === CARIBBEAN_CUSTOM_LABEL) {
        customLabel = CARIBBEAN_CUSTOM_LABEL;
      } else if (item.regionCode === CENTRAL_AMERICA_CODE) {
        customLabel = CENTRAL_AMERICA_CUSTOM_LABEL;
      } else if (item.regionCode === AUSTRALIA_CODE) {
        customLabel = AUSTRALIA_CUSTOM_LABEL;
      } else if (item.regionCode === USA_CODE) {
        customLabel = USA_CUSTOM_LABEL;
      }
      regionDataToStore.push({
        label: item.regionTagTitle,
        count: 0,
        code: item.regionCode,
        latitude: Number(item.regionLatitude),
        longitude: Number(item.regionLongitude),
        customLabel: customLabel,
      });
    });
    regionDataToStore.sort(customSort(currentLocale));

    setHotelDirectoryState([LEISURE_REGION_LIST, SELECTED_REGION], {
      [LEISURE_REGION_LIST]: {
        [LEISURE_REGION_LIST]: regionDataToStore ? regionDataToStore : [],
      },
      [SELECTED_REGION]: { [SELECTED_REGION]: regionDataToStore[0] },
    });
    const inputValues: UXLPayload = {
      options: {
        countsOnly: true,
      },
      facets: {
        terms: [
          {
            type: SearchFacetsList?.brands,
            dimensions:
              filtersSessionVal && filtersSessionVal?.brands && Object.keys(filtersSessionVal?.brands)?.length > 0
                ? filtersSessionVal.brands
                : [],
          },
          {
            type: SearchFacetsList?.amenities,
            dimensions:
              filtersSessionVal && filtersSessionVal?.amenities && Object.keys(filtersSessionVal?.amenities)?.length > 0
                ? filtersSessionVal.amenities
                : [],
          },
          {
            type: SearchFacetsList?.propertyType,
            dimensions:
              filtersSessionVal &&
              filtersSessionVal?.propertyType &&
              Object.keys(filtersSessionVal?.propertyType)?.length > 0
                ? filtersSessionVal.propertyType
                : [],
          },
          {
            type: SearchFacetsList?.activities,
            dimensions:
              filtersSessionVal &&
              filtersSessionVal?.activities &&
              Object.keys(filtersSessionVal?.activities)?.length > 0
                ? filtersSessionVal.activities
                : [],
          },
          {
            type: SearchFacetsList?.state,
            dimensions:
              filtersSessionVal && filtersSessionVal?.states && Object.keys(filtersSessionVal?.states)?.length > 0
                ? filtersSessionVal.states
                : [],
          },
          {
            type: SearchFacetsList?.country,
            dimensions: [],
          },
          {
            type: SearchFacetsList?.events,
            dimensions:
              filtersSessionVal && filtersSessionVal?.events && Object.keys(filtersSessionVal?.events)?.length > 0
                ? filtersSessionVal.events
                : [],
          },
          {
            type: SearchFacetsList?.transportation,
            dimensions:
              filtersSessionVal &&
              filtersSessionVal?.transportationTypes &&
              Object.keys(filtersSessionVal?.transportationTypes)?.length > 0
                ? filtersSessionVal.transportationTypes
                : [],
          },
          {
            type: SearchFacetsList?.leisureRegion,
            dimensions: [],
          },
        ],
      },
    };
    getInitialRegionCount({
      variables: {
        search: inputValues,
      },
      context: {
        headers: {
          'x-request-id': requestId,
          'accept-language': currentLocale?.replace('_', '-'),
        },
      },
    }).catch(() => {
      setDisableFilters(true);
    });
    // call datalayer on page load to add/remove some datalayer attributes specific for HD page
    !isStoryBook && getLatestSessionData();
  }, []);

  const [initialResponse, setInitialResponse] = useState<any>();

  useEffect(() => {
    if (!isFiltersApplied) {
      const inputValues: UXLPayload = {
        options: {
          countsOnly: true,
        },
        facets: {
          terms: [
            {
              type: SearchFacetsList?.brands,
              dimensions: [],
            },
            {
              type: SearchFacetsList?.amenities,
              dimensions: [],
            },
            {
              type: SearchFacetsList?.propertyType,
              dimensions: [],
            },
            {
              type: SearchFacetsList?.activities,
              dimensions: [],
            },
            {
              type: SearchFacetsList?.state,
              dimensions: [],
            },
            {
              type: SearchFacetsList?.country,
              dimensions: [],
            },
            {
              type: SearchFacetsList?.events,
              dimensions: [],
            },
            {
              type: SearchFacetsList?.transportation,
              dimensions: [],
            },
            {
              type: SearchFacetsList?.leisureRegion,
              dimensions: [],
            },
          ],
        },
      };
      getInitialResponse({
        variables: {
          search: inputValues,
        },
        context: {
          headers: {
            'x-request-id': requestId,
            'accept-language': currentLocale?.replace('_', '-'),
          },
        },
      }).catch(() => {
        setDisableFilters(true);
      });
    }
  }, []);

  // get the full response containing all filters
  async function getInitialResponse(inputValues: any) {
    const data = await fetchHDData(inputValues);
    if (initialResponse === undefined) {
      setInitialResponse(extractFiltersData(data, brandsLabel));
    }
    const fusionIdValue = data?.search?.properties?.searchByLocation?.searchQueryId ?? '';

    // call datalayer on filter interactions
    updateDataLayerOnInteractions(
      resolvedUrl,
      headersData,
      query,
      datalayerParams,
      currentLocale,
      sessionData?.sessionToken,
      '',
      fusionIdValue
    );
  }

  useEffect(() => {
    // Note: Removed clustererRef.repaint() call - clusterer is now managed internally by child component
    setMarkers(stateORCountryList);
  }, [stateORCountryList]);

  // Note: Removed useEffect for markers repaint - no longer needed as clusterer is managed internally
  useEffect(() => {
    const newFiltersPayload = filtersPayload?.map((fp: any) => {
      const returnValue = { ...fp };
      return returnValue;
    });

    setFiltersPayload(newFiltersPayload);

    setCenter({
      lat: selectedRegion?.latitude,
      lng: selectedRegion?.longitude,
    });
  }, [selectedRegion]);

  useEffect(() => {
    selectedRegionRef.current = selectedRegion;
    filtersPayloadRef.current = filtersPayload;

    const inputValues: UXLPayload = {
      options: {
        countsOnly: true,
      },
      facets: {
        terms: filtersPayload,
      },
    };

    getRegionAndFiltersData({
      variables: {
        search: inputValues,
      },
      context: {
        headers: {
          'x-request-id': requestId,
          'accept-language': currentLocale?.replace('_', '-'),
        },
      },
    }).catch(() => {
      setMapZoom(DEFAULT_ZOOM_LEVEL);
      setCenter({
        lat: selectedRegion?.latitude,
        lng: selectedRegion?.longitude,
      });
      setDisableFilters(true);
    });
  }, [filtersPayload]);

  useEffect(() => {
    //enabling session update for MapAndRegionCard component with filters in query on mount
    //HotelDirectoryMap component useeffect trigger first before MapAndRegionCard useffect
    if (applyBtnClicked) {
      const filtersPayloadWithEmptyLeisureRegion = filtersPayload?.map((fp: any) => {
        const returnValue = { ...fp };

        if (fp?.type === 'LEISURE_REGIONS') {
          returnValue.dimensions = [];
        }

        return returnValue;
      });
      const inputData: UXLPayload = {
        options: {
          countsOnly: true,
        },
        facets: {
          terms: filtersPayloadWithEmptyLeisureRegion,
        },
      };
      getRegionCountOnApply({
        variables: {
          search: inputData,
        },
        context: {
          headers: {
            'x-request-id': requestId,
            'accept-language': currentLocale?.replace('_', '-'),
          },
        },
      });
      updateURLParams();
      !isStoryBook && getLatestSessionData();
      updateExitPointLink();
      // update pills selection on click of apply button
      setPillsData([
        {
          label: filterLabel,
          value: 'allFilters',
          isActive: filtersCount > 0,
          showIcon: filtersCount === 0,
          iconClass: 'icon-dining-filter',
          count: filtersCount,
          custom_click_track_value: `${HOTEL_DIRECTORY} | ${ALL_FILTERS} | internal`,
          buttonClassName: 'd-flex align-items-center all-filters-button',
          id: 'allFilters',
        },
        {
          label: amenitiesLabel,
          value: 'amenities',
          isActive: isAmenitiesSelected,
          custom_click_track_value: `${HOTEL_DIRECTORY} | ${AMENITIES_FILTERS} | internal`,
          className: totalCountOfHotels === 0 ? 'd-none' : '',
          id: 'amenities',
        },
        {
          label: brandsLabel,
          value: 'brands',
          isActive: isBrandsSelected,
          custom_click_track_value: `${HOTEL_DIRECTORY} | ${BRANDS_FILTERS} | internal`,
          id: 'brands',
        },
      ]);
    }
    applyBtnClicked && setIsLoading(true);
  }, [applyBtnClicked]);

  // add below useEffect to stop the background scroll when the filter popup is open
  useEffect(() => {
    if (!popupOpenState && bodyRef.current) {
      if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(MAP_VIEW_SESSION_KEY) !== 'true') {
        bodyRef.current.style.overflow = 'auto';
      }
    } else {
      if (bodyRef.current) {
        bodyRef.current.style.overflow = 'hidden';
      }
    }
  }, [popupOpenState]);

  // add class to position full screen view icon properly on map
  // This QuerySelector can't be removed, as this class is from Google maps library.
  const fullViewElement = document.querySelector('.gm-fullscreen-control');
  if (fullViewElement) {
    fullViewElement?.parentElement?.classList?.add('map-full-view-container');
  }

  const fetchHDData = async (inputValues: any) => {
    const { data } = isAuthorMode
      ? hotelDirectoryMockUXL
      : await client.query({
          query: phoenixShopSearchPropertiesByLocationInput,
          ...inputValues,
        });
    return data;
  };

  const prevSelectedRegion = selectedRegionRef.current;
  const prevFiltersPayload = filtersPayloadRef.current;

  async function getInitialRegionCount(inputValues: any) {
    const data = await fetchHDData(inputValues);

    const fusionIdValue = data?.search?.properties?.searchByLocation?.searchQueryId ?? '';

    if (data?.search?.properties?.searchByLocation?.facets?.length === 0) {
      setDisableFilters(true);
    }
    if (data?.search?.properties?.searchByLocation?.facets?.length) {
      if (initialResponse === undefined) {
        setInitialResponse(extractFiltersData(data, brandsLabel));
      }
      setTotalCountOnHeader(
        formatNumberByLocale(data?.search?.properties?.searchByLocation?.total, currentLocale?.replace('_', '-'))
      );
      setTotalCountOfHotels(data?.search?.properties?.searchByLocation?.total);
      setHotelDirectoryState([TOTAL_NO_OF_HOTELS_WORLDWIDE], {
        [TOTAL_NO_OF_HOTELS_WORLDWIDE]: {
          [TOTAL_NO_OF_HOTELS_WORLDWIDE]: data?.search?.properties?.searchByLocation?.total,
        },
      });

      const regionDataToStore: HotelDirectoryDataList = getRegionDataToStore(
        regionName,
        subRegionName,
        data,
        model.mexicoLabel,
        model.canadaLabel
      );
      regionDataToStore.sort(customSort(currentLocale));

      setHotelDirectoryState([LEISURE_REGION_LIST, SELECTED_REGION], {
        [LEISURE_REGION_LIST]: {
          [LEISURE_REGION_LIST]: regionDataToStore ? regionDataToStore : [],
        },
        [SELECTED_REGION]: { [SELECTED_REGION]: regionDataToStore[0] },
      });

      const filtersDataFromResponse: any = extractFiltersData(data, brandsLabel);
      setFiltersData(filtersDataFromResponse);
      if (filtersDataFromResponse.length) {
        setHotelDirectoryState([SHOW_REGION_CARD_ERROR], {
          [SHOW_REGION_CARD_ERROR]: { [SHOW_REGION_CARD_ERROR]: true },
        });
      }
    }
    updateDataLayerOnInteractions(
      resolvedUrl,
      headersData,
      query,
      datalayerParams,
      currentLocale,
      sessionData?.sessionToken,
      '',
      fusionIdValue
    );
  }

  // get the region and filters data on selection
  async function getRegionAndFiltersData(inputValues: any) {
    const data = await fetchHDData(inputValues);

    setMapZoom(DEFAULT_ZOOM_LEVEL);
    if (data) {
      setIsDataLoaded(true);
      setIsLoading(false);
      setTotalCountOfHotels(data?.search?.properties?.searchByLocation?.total);

      if (prevSelectedRegion !== selectedRegion || applyBtnClicked) {
        const subRegionDataFromResponse = extractMarkerData(
          data,
          selectedRegion,
          subRegionName?.subRegion,
          labelForChina,
          model.australiaPILabel,
          model.mexicoLabel,
          model.canadaLabel,
          model.australiaAndOceaniaLabel
        );
        setHotelDirectoryState([STATE_COUNTRY_LIST], {
          [STATE_COUNTRY_LIST]: { [STATE_COUNTRY_LIST]: subRegionDataFromResponse ? subRegionDataFromResponse : [] },
        });
      }

      // set filter response for dyncamic filter changes
      if (prevFiltersPayload !== filtersPayload && data?.search?.properties?.searchByLocation?.facets?.length > 0) {
        const filtersDataFromResponse: any = extractFiltersData(data, brandsLabel);
        setFiltersData(filtersDataFromResponse);
      }
    } else {
      setIsLoading(true);
      setIsDataLoaded(false);
      setHotelDirectoryState([STATE_COUNTRY_LIST], {
        [STATE_COUNTRY_LIST]: { [STATE_COUNTRY_LIST]: [] },
      });
    }
  }

  // get the region related details on click of apply button on modal
  async function getRegionCountOnApply(inputValues: any) {
    const data = await fetchHDData(inputValues);

    if (data) {
      setTotalCountOnHeader(
        formatNumberByLocale(data?.search?.properties?.searchByLocation?.total, currentLocale?.replace('_', '-'))
      );
      setIsLoading(false);
      setIsDataLoaded(true);
      setCenter({
        lat: selectedRegion?.latitude,
        lng: selectedRegion?.longitude,
      });

      const subRegionDataFromResponse = extractMarkerData(
        data,
        selectedRegion,
        subRegionName?.subRegion,
        labelForChina,
        model.australiaPILabel,
        model.mexicoLabel,
        model.canadaLabel,
        model.australiaAndOceaniaLabel
      );
      setHotelDirectoryState([STATE_COUNTRY_LIST], {
        [STATE_COUNTRY_LIST]: { [STATE_COUNTRY_LIST]: subRegionDataFromResponse ? subRegionDataFromResponse : [] },
      });
      const regionDataToStore: HotelDirectoryDataList = getRegionDataToStore(
        regionName,
        subRegionName,
        data,

        model.mexicoLabel,
        model.canadaLabel
      );
      regionDataToStore.sort(customSort(currentLocale));

      setHotelDirectoryState([LEISURE_REGION_LIST, SELECTED_REGION], {
        [LEISURE_REGION_LIST]: {
          [LEISURE_REGION_LIST]: regionDataToStore ? regionDataToStore : [],
        },
        [SELECTED_REGION]: { [SELECTED_REGION]: regionDataToStore[0] },
      });

      const fusionIdValue = data?.search?.properties?.searchByLocation?.searchQueryId ?? '';

      // call datalayer on filter interactions
      updateDataLayerOnInteractions(
        resolvedUrl,
        headersData,
        query,
        datalayerParams,
        currentLocale,
        sessionData?.sessionToken,
        '',
        fusionIdValue
      );

      // store the flter response on click of apply button when filter response is empty
      if (data?.search?.properties?.searchByLocation?.facets?.length === 0 && applyBtnClicked) {
        const filtersDataFromResponse: any = extractFiltersData(data, brandsLabel);
        setFiltersData(filtersDataFromResponse);
        setDisableFilters(true);
      } else if (data?.search?.properties?.searchByLocation?.facets?.length > 0 && applyBtnClicked) {
        setDisableFilters(false);
      }
    } else {
      setIsLoading(true);
      setIsDataLoaded(false);
    }
    setApplyBtnClicked(false);
  }

  // Asynchronous function to fetch and update the latest session data
  const getLatestSessionData = async () => {
    await updateSessionProperties(
      sessionData?.sessionToken,
      SESSION_APP_GET_CALL_URL,
      SESSION_GET_CALL_URL_CLIENT,
      NEXT_PUBLIC_PREFIX,
      filtersPayload,
      datalayerProperties
    );
  };

  const tooltipDialogElementRef = useRef<HTMLDivElement | null>(null);
  const tooltipRefElement = useRef<HTMLSpanElement | null>(null);
  const [tooltipContent, setTooltipContent] = useState<tooltipContentProps>({});
  const [showToolTip, setShowToolTip] = useState(false);
  const [tooltipModalPosition, setTooltipModalPosition] = useState('top');

  const modalRefElement = useRef<any>();

  useEffect(() => {
    const tooltipDialogElem = tooltipDialogElementRef?.current;
    if (!showToolTip || !tooltipRefElement?.current || !tooltipDialogElem) {
      if (tooltipDialogElem) {
        tooltipDialogElem.style.visibility = 'hidden';
        setTooltipPosition({
          offsetLeft: 0,
          offsetTop: 0,
          tooltipDialogElemOffsetHeight: 0,
          tooltipDialogElemOffsetWidth: 0,
          anchorOffset: 0,
        });
      }
      return;
    }

    // Defer measurement so ref is assigned and DOM has settled after setTooltipContent/setShowToolTip.
    // Double rAF ensures layout is complete (icon ref and tooltip div measured after paint).
    let raf2: number | undefined;
    const rafId = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const icon = tooltipRefElement.current;
        const elem = tooltipDialogElementRef?.current;
        if (!icon || !elem) return;

        const modalRect = elem.getBoundingClientRect();
        const tooltipIconrect = icon.getBoundingClientRect();

        let offsetLeftValue = tooltipIconrect.left + window.scrollX;
        const offsetTopValue = tooltipIconrect.top;
        const rightOverflow = offsetLeftValue + modalRect.width / 2 - window.innerWidth;

        if (rightOverflow > 0) {
          offsetLeftValue -= rightOverflow;
        } else if (offsetLeftValue - elem.offsetWidth / 2 - 2 < 0) {
          offsetLeftValue = elem.offsetWidth / 2 + 4;
        }
        const iconCenterX = tooltipIconrect.left + tooltipIconrect.width / 2 + window.scrollX;
        const modalLeft = offsetLeftValue;
        const modalWidth = modalRect.width;
        const anchorOffset = iconCenterX - modalLeft + modalWidth / 2;

        setTooltipPosition({
          offsetLeft: offsetLeftValue,
          offsetTop: offsetTopValue,
          tooltipDialogElemOffsetHeight: elem.offsetHeight,
          tooltipDialogElemOffsetWidth: elem.offsetWidth,
          anchorOffset: anchorOffset,
        });
        if (offsetTopValue - elem.offsetHeight < 0) {
          setTooltipModalPosition('bottom');
        } else {
          setTooltipModalPosition('top');
        }
      });
    });
    return () => {
      cancelAnimationFrame(rafId);
      if (raf2 !== undefined) cancelAnimationFrame(raf2);
    };
  }, [tooltipContent, showToolTip]);

  useEffect(() => {
    const tooltipDialogElem = tooltipDialogElementRef?.current;
    if (tooltipDialogElem) {
      if (
        !tooltipContent?.tooltipHeading ||
        tooltipPosition.offsetLeft === 0 ||
        tooltipPosition.offsetTop === 0 ||
        !showToolTip
      ) {
        tooltipDialogElem.style.visibility = 'hidden';
      } else {
        tooltipDialogElem.style.visibility = 'visible';
      }
    }
  }, [tooltipPosition, tooltipContent, showToolTip]);

  return (
    <StyledHotelDirectoryMap
      offsetLeft={tooltipPosition?.offsetLeft}
      offsetTop={tooltipPosition?.offsetTop}
      tooltipDialogElemOffsetHeight={tooltipPosition?.tooltipDialogElemOffsetHeight}
      tooltipDialogElemOffsetWidth={tooltipPosition?.tooltipDialogElemOffsetWidth}
      anchorOffset={tooltipPosition?.anchorOffset}
      tooltipModalPosition={tooltipModalPosition}
    >
      {popupOpenState && (
        <>
          {/* Screen reader: aria-live announces tooltip content for NVDA */}
          <div aria-live="polite" aria-atomic="true" className="sr-only">
            {showToolTip && tooltipContent?.tooltipHeading && tooltipContent?.tooltipText
              ? `${tooltipContent.tooltipHeading} ${tooltipContent.tooltipText}`
              : ''}
          </div>
          {/* // Tooltip to be shown in tooltip amenities */}
          <div className="tooltip-dialog" ref={tooltipDialogElementRef} role="tooltip" id={HOTEL_DIRECTORY_TOOLTIP_ID}>
            <div className={`m-popover-${tooltipModalPosition} inverse`} data-content="Inverse Theme">
              <div className="m-popover-header">
                <h2 className="t-subtitle-l mb-0">{tooltipContent?.tooltipHeading}</h2>
              </div>
              <p className="t-font-s mb-0">{tooltipContent?.tooltipText ?? ''}</p>
            </div>
          </div>
          <SearchFilterPanel
            popupOpenState={popupOpenState}
            setPopupOpenState={setPopupOpenState}
            filtersData={
              filterButtonClicked?.['allFilters']
                ? filtersData
                : filterButtonClicked?.['amenities']
                ? amenitiesData
                : brandsData
            }
            primaryButtonLabel={applyLabel}
            secondaryButtonLabel={clearLabel}
            filterOrder={filtersOrderList}
            filtersPayload={filtersPayload}
            setFiltersPayload={setFiltersPayload}
            dynamicFiltersEnabled={true}
            setFiltersCount={setFiltersCount}
            setApplyBtnClicked={setApplyBtnClicked}
            brandCategories={brandCategories}
            clickTrackingLoc={HOTEL_DIRECTORY}
            filtersHeaderLabel={
              filterButtonClicked?.['allFilters']
                ? filterLabel
                : filterButtonClicked?.['amenities']
                ? amenitiesLabel
                : brandsLabel
            }
            onApplyButtonClicked={() => {
              searchCounterValue(headersData, props.hotelDirectoryCounter, props.setHotelDirectoryCounter);
            }}
            filterButtonClicked={filterButtonClicked}
            setFilterButtonClicked={setFilterButtonClicked}
            initialResponse={initialResponse}
            showMoreLabel={showMoreLabel}
            showLessLabel={showLessLabel}
            logoContainerWidth={isDesktopView ? 160 : isViewportM ? 95 : 80}
            memberLoungeTooltipLabel={memberLoungeTooltipLabel}
            tooltipRefElement={tooltipRefElement}
            setTooltipContent={setTooltipContent}
            isViewportM={isViewportM}
            modalRefElement={modalRefElement}
            setShowToolTip={setShowToolTip}
            tooltipContainerId={HOTEL_DIRECTORY_TOOLTIP_ID}
          />
        </>
      )}
      {!isMapLoaded || isLoading ? <SkeletonLoader></SkeletonLoader> : <span></span>}

      {!isLoading && (
        <MapClusterComponent
          center={center}
          markers={markers}
          mapHeight={500}
          zoom={mapZoom}
          setCenter={setCenter}
          onZoomChange={setMapZoom}
          isMapLoaded={isMapLoaded}
          setIsMapLoaded={setIsMapLoaded}
          isDesktopView={isDesktopView}
          selectedRegion={selectedRegion}
          stateORCountryList={stateORCountryList}
          isFiltersApplied={isFiltersApplied}
          infoWindowData={infoWindowData}
          openInfoWindow={openInfoWindow}
          setOpenInfoWindow={setOpenInfoWindow}
          setInfoWindowData={setInfoWindowData}
        />
      )}
    </StyledHotelDirectoryMap>
  );
};

export default HotelDirectoryMap;
